/**
 * Technology page.
 *
 * Was homepage-anchor-only (#technology) — the only nav item without a
 * dedicated route once Services/About got their own pages. Content reuses
 * the same translation keys as TechnologySection so the two never drift
 * into disagreement about what the clinic's technology actually is.
 */

import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { clinicNode } from "@/lib/schema";
import { motion } from "framer-motion";
import { Scan, Smile, CheckCircle2 } from "lucide-react";
import { InteractiveIcon } from "@/components/AnimatedIcons";

interface TechnologyPageProps {
  locale: Locale;
}

export default function TechnologyPage({ locale }: TechnologyPageProps) {
  const { t, language } = useLanguage();

  const title = locale === "ar" ? "التقنية" : "Technology";
  const description = t("techDesc");

  const technologies = [
    {
      icon: Scan,
      title: t("scannerTitle"),
      description: t("scannerDesc"),
      benefits: [t("scannerBenefit1"), t("scannerBenefit2"), t("scannerBenefit3"), t("scannerBenefit4")],
    },
    {
      icon: Smile,
      title: t("smileDesignTitle"),
      description: t("smileDesignDesc"),
      benefits: [t("smileBenefit1"), t("smileBenefit2"), t("smileBenefit3"), t("smileBenefit4")],
    },
  ];

  const stats = [
    { value: "20+", label: t("yearsExpLabel") },
    { value: "10K+", label: t("happyPatientsLabel") },
    { value: "100%", label: t("sterilizationLabel") },
    { value: "6", label: t("specialtiesLabel") },
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/technology`}
        locale={locale}
        schema={clinicNode(locale)}
      />
      <main className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="mb-12">
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            {t("technologyTitle")}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            {language === "ar" ? (
              <>
                {t("digitalDentistry")} <span className="text-gradient">{t("advancedDigital")}</span>
              </>
            ) : (
              <>
                {t("advancedDigital")} <span className="text-gradient">{t("digitalDentistry")}</span>
              </>
            )}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">{description}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
          {technologies.map((tech) => (
            <motion.div
              key={tech.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="glass rounded-3xl p-8 border-border/50"
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-6">
                <InteractiveIcon icon={tech.icon} className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{tech.title}</h2>
              <p className="text-slate-300 mb-8 leading-relaxed">{tech.description}</p>
              <ul className="space-y-3">
                {tech.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3">
                    <InteractiveIcon icon={CheckCircle2} className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-slate-100 font-medium">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center glass rounded-2xl p-6 border-border/30">
              <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </main>
    </>
  );
}
